import type { ReactNode } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import {
  eventMeta,
  TASK_CLASS_META,
  TASK_STATUS_META,
  type MaintenanceTask,
} from '@/types/maintenanceTask'
import { useEventTypeCatalog } from '@/hooks/useEventTypes'

function fmtDate(iso?: string): string {
  if (!iso) return '—'
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='flex gap-3'>
      <span className='w-28 shrink-0 text-xs font-medium text-gray-400'>{label}</span>
      <span className='text-sm text-gray-800'>{children}</span>
    </div>
  )
}

/** Read-only detail view for a worklist task (the row's info action). */
export function EventDetailsDialog({ task, onClose }: { task: MaintenanceTask | null; onClose: () => void }) {
  // The tenant's name for the type, never its code (z8nrz7fb9k).
  const catalog = useEventTypeCatalog()
  const evt = task ? eventMeta(task.eventType, catalog) : null
  const cls = task ? TASK_CLASS_META[task.taskClass] : null
  const status = task ? TASK_STATUS_META[task.status] : null

  return (
    <Dialog open={task !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        {task && evt && cls && status && (
          <>
            <div className='flex items-center justify-between gap-3'>
              <h2 className='text-base font-semibold text-gray-900'>{task.equipmentId}</h2>
              <span
                className='inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold'
                style={{ backgroundColor: `${status.color}14`, color: status.color }}
              >
                <span className='h-1.5 w-1.5 rounded-full' style={{ backgroundColor: status.color }} />
                {status.label}
              </span>
            </div>

            <div className='mt-4 space-y-2.5'>
              <Row label='Event type'>
                <span className='inline-flex items-center gap-2'>
                  <span className='h-2 w-2 rounded-full' style={{ backgroundColor: evt.color }} />
                  {evt.label}
                </span>
              </Row>
              <Row label='Class'>{cls.label}</Row>
              <Row label='Used by'>{task.usedBy || '—'}</Row>
              <Row label='Priority'>{task.priority ?? '—'}</Row>
              <Row label='Early start'>{fmtDate(task.earlyStart)}</Row>
              <Row label='Due date'>{fmtDate(task.dueDate)}</Row>
              <Row label='Planned hrs'>{task.planHours.toFixed(1)}</Row>
              <Row label='Assigned'>{task.assigned}</Row>
              <Row label='SOP'>
                <span className='font-mono text-xs text-gray-700'>{task.sopRef || 'No SOP linked'}</span>
              </Row>
            </div>

            <div className='mt-6 flex justify-end'>
              <button
                type='button'
                onClick={onClose}
                className='rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
              >
                Close
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
